import iv from '~lib/common/config/iv';

function validatIVOnAtStart(caseConf) {
  const errors = [];
  const initIvs = caseConf.initialIvs || [];
  const siteMap = {};
  const categoryMap = {};

  // Make map of location|side => count to detect multiple IVs using the same site
  for (const initIv of initIvs) {
    const mapKey = `${initIv.site}|${initIv.side}`;
    if (!siteMap[mapKey]) {
      siteMap[mapKey] = 1;
    } else {
      siteMap[mapKey] += 1;
    }
  }

  // Make map of category|location|side => count to detect dialysis catheter issues
  for (const initIv of initIvs) {
    const mapKey = `${initIv.categoryType}|${initIv.site}|${initIv.side}`;
    if (!siteMap[mapKey]) {
      categoryMap[mapKey] = 1;
    } else {
      categoryMap[mapKey] += 1;
    }
  }

  for (const initIv of initIvs) {
    // Multiple IVs using the same site
    if (siteMap[`${initIv.site}|${initIv.side}`] > 1) {
      errors.push(
        ivOnAtStartError('On at Start: Multiple IVs using the same location and side', initIv.id, 'siteConflict')
      );
    }

    if (iv.categories.find(category => category.key === initIv.categoryType)) {
      // Only check the following errors for IV categories that are in the IV setup

      // Port amount enabled
      const peripheralType = caseConf.iv.types.find(ivType => ivType.type === 'PERIPHERAL');
      if (peripheralType && !peripheralType.ports.includes(initIv.portAmount)) {
        errors.push(
          ivOnAtStartError('On at Start: Port amount not enabled in IV', initIv.id, 'portAmountEnabled')
        );
      }

      // Location enabled
      const ivType = caseConf.iv.types.find(ivType => ivType.type === initIv.categoryType);
      if (!ivType || !ivType.sites.find(ivSite => ivSite.site === initIv.site)) {
        errors.push(
          ivOnAtStartError('On at Start: Location not enabled in IV', initIv.id, 'locationEnabled')
        );
      }

      // Side enabled
      const ivSite = ivType && ivType.sites.find(ivSite => ivSite.site === initIv.site);
      if (ivSite && !ivSite.sides.includes(initIv.side)) {
        errors.push(
          ivOnAtStartError('On at Start: Side not enabled in IV', initIv.id, 'sideEnabled')
        );
      }

      // Needle enabled
      if (ivType && !ivType.gauges.includes(initIv.needle)) {
        errors.push(
          ivOnAtStartError('On at Start: Needle not enabled in IV', initIv.id, 'needleEnabled')
        );
      }
    }
  }

  // Internal Jugular and Subclavian on the same side for dialysis catheter
  if (
    categoryMap['DIALYSIS_CATHETER|INTERNAL_JUGULAR|LEFT'] && categoryMap['DIALYSIS_CATHETER|SUBCLAVIAN|LEFT'] ||
    categoryMap['DIALYSIS_CATHETER|INTERNAL_JUGULAR|RIGHT'] && categoryMap['DIALYSIS_CATHETER|SUBCLAVIAN|RIGHT']
  ) {
    errors.push(
      ivOnAtStartError('On at Start: IJ and Subclavian cannot be placed on the same side', null, 'ijSubclavian')
    );
  }

  // "The same IV type can't go on the same body part, but different IV types could go
  //  on a same body part if it is on the other side."
  for (let i = 0; i < initIvs.length; i++) {
    for (let j = i + 1; j < initIvs.length; j++) {
      const initIv1 = initIvs[i];
      const initIv2 = initIvs[j];
      if (!isValidIvPlacement(
        initIv1.categoryType, initIv2.categoryType,
        initIv1.site, initIv2.site,
        initIv1.side, initIv2.side
      )) {
        errors.push(
          ivOnAtStartError('The same IV type can\'t go on the same body part,', null, 'sameBodyPart')
        );
      }
    }
  }

  return {
    errors,
    warnings: [],
  };
}

function isValidIvPlacement(ivCategoryType1, ivCategoryType2, ivSite1, ivSite2, sideType1, sideType2) {
	if (isLimb(toPhysicalAssessmentLocation(ivSite1, hasSides(ivSite1) ? 'LEFT' : 'BOTH'))) { //side left/right doesn't matter for physicalAssessmentLocation since just checking if a limb or not
		if(ivSite1 == ivSite2) { //limbs don't have left/right on the same location (left/right are different limbs), so never ok to put any type on the same site/side
			return sideType1 !== sideType2;
		}
	} else { //non-limb
    // console.log('  else')
		if(ivCategoryType1 === ivCategoryType2) {
			return !isNonLimbSameSite(ivSite1, ivSite2);
		}
		if(isNonLimbSameSite(ivSite1, ivSite2)) {
			return sideType1 !== sideType2;
		}
	}
	return true;
}

function isNonLimbSameSite(ivSite1, ivSite2) {
	if (ivSite1 === 'IV_EJ' || ivSite1 === 'INTERNAL_JUGULAR') {
		return ivSite2 === 'IV_EJ' || ivSite2 === 'INTERNAL_JUGULAR';
	}
	return ivSite1 === ivSite2;
}

function toPhysicalAssessmentLocation(ivSite, sideType) {
	if (sideType === 'NULL' || sideType === 'ANY') {
		return 'NULL';
	}
	if (hasSides(ivSite)) {
		if (sideType === 'BOTH') {
			return 'NULL';
		}
	} else if (sideType === 'LEFT' || sideType === 'RIGHT') {
		return 'NULL';
	}
	switch (ivSite) {
    case 'IV_SCALP':
      return 'HEAD';
    case 'IV_EJ':
    case 'INTERNAL_JUGULAR':
      return 'NECK';
    case 'IV_ANTECUBITAL':
    case 'IO_HUMERUS':
    case 'UPPER_ARM':
      return sideType === 'LEFT' ? 'LEFT_ARM_UPPER' : 'RIGHT_ARM_UPPER';
    case 'IV_FOREARM':
      return sideType === 'LEFT' ? 'LEFT_ARM_LOWER' : 'RIGHT_ARM_LOWER';
    case 'IV_HAND':
      return sideType === 'LEFT' ? 'LEFT_HAND' : 'RIGHT_HAND';
    case 'IV_FOOT':
      return sideType === 'LEFT' ? 'LEFT_FOOT' : 'RIGHT_FOOT';
    case 'IO_STERNUM':
    case 'SUBCLAVIAN':
    case 'CHEST_WALL':
      return 'CHEST';
    case 'IO_ILIAC_CREST':
      return 'ABDOMEN';
    case 'FEMORAL':
      return 'GENITALIA';
    case 'IO_DISTAL_FEMUR':
    case 'IO_PROXIMAL_TIBIA':
    case 'IO_DISTAL_TIBIA':
      return sideType === 'LEFT' ? 'LEFT_LEG_LOWER' : 'RIGHT_LEG_LOWER';
    default:
	}
	return 'NULL';
}

function hasSides(ivSite) {
	switch(ivSite) {
    case 'IV_SCALP':
    case 'IO_STERNUM':
      return false;
	}
	return true;
}

function isLimb(physicalAssessmentLocation) {
  switch (physicalAssessmentLocation) {
    case 'RIGHT_ARM_UPPER':
    case 'RIGHT_ARM_LOWER':
    case 'RIGHT_HAND':
    case 'LEFT_ARM_UPPER':
    case 'LEFT_ARM_LOWER':
    case 'LEFT_HAND':
    case 'RIGHT_LEG_UPPER':
    case 'RIGHT_LEG_LOWER':
    case 'RIGHT_FOOT':
    case 'LEFT_LEG_UPPER':
    case 'LEFT_LEG_LOWER':
    case 'LEFT_FOOT':
      return true;
  }
  return false;
}

function ivOnAtStartError(message, id, type) {
  return {
    message,
    category: 'ivs',
    field: 'initialIvs',
    id,
    metadata: {
      field: 'initialIvs',
      id,
      type,
      scope: 'control',
    },
  };
}

export default validatIVOnAtStart;
